import React, { useEffect, useState } from 'react';
import { Grid, CircularProgress } from "@mui/material";
import Storefront from "../../../../assets/images/icons/StoreFront.svg";
import FaiBox from '../../../../MuiComponents/FaiBox';
import FaiTypography from '../../../../MuiComponents/FaiTypography';
import FaiBadge from '../../../../MuiComponents/FaiBadge';
import { InventoryCard } from '../InventoryCard';
import DataTable from '../../../../components/DataTable';
import theme from '../../../../assets/theme';
import { Card } from '../Card';
import OrdersController from '../../../../controllers/orderController';
import { ProductController } from '../../../../controllers/productController';

type CountAmount = { count: number; amount: number };

type DashboardSummary = {
    today: CountAmount;
    yesterday: CountAmount;
    thisMonth: CountAmount;
    allTime: CountAmount;
    pendingOrdersCount: number;
};

type InventorySummary = {
    totalSkus: number;
    outOfStockCount: number;
    pendingApprovalCount: number;
};

const emptyCountAmount: CountAmount = { count: 0, amount: 0 };

const Dashboard: React.FC = () => {
    const { typography, palette } = theme;
    const [orderSummary, setOrderSummary] = useState<DashboardSummary | null>(null);
    const [inventory, setInventory] = useState<InventorySummary | null>(null);
    const [recentOrders, setRecentOrders] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            try {
                const [summaryRes, inventoryRes, ordersRes] = await Promise.all([
                    OrdersController.getMerchantDashboardSummary(),
                    ProductController.getMerchantInventorySummary(),
                    OrdersController.getMerchantOrders(0, 10),
                ]);
                if (!mounted) return;
                setOrderSummary(summaryRes.data?.data || null);
                setInventory(inventoryRes.data?.data || null);
                const rows = (ordersRes.data?.data?.data || []).map((sub: any) => {
                    const items: any[] = Array.isArray(sub.items) ? sub.items : [];
                    const first = items[0];
                    const productLabel = first
                        ? items.length > 1
                            ? `${first.product?.productName || '—'} +${items.length - 1} more`
                            : first.product?.productName || '—'
                        : '—';
                    const totalQty = items.reduce((sum, i) => sum + (i.quantity || 0), 0);
                    return {
                        id: sub.id,
                        product: productLabel,
                        quantity: totalQty,
                        amount: `Rs. ${Number(sub.totalPrice || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`,
                        paymentMode: sub.paymentMode || '—',
                        status: sub.status || '—',
                        date: sub.createdAt ? new Date(sub.createdAt).toLocaleDateString() : '—',
                    };
                });
                setRecentOrders(rows);
            } catch (error) {
                console.error('Failed to load dashboard:', error);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();
        return () => {
            mounted = false;
        };
    }, []);

    const paymentCards = [
        {
            title: "Today's Orders",
            bgColor: palette.teal.main,
            data: orderSummary?.today || emptyCountAmount,
        },
        {
            title: "Yesterday's Orders",
            bgColor: "#3190D0",
            data: orderSummary?.yesterday || emptyCountAmount,
        },
        {
            title: "This Month",
            bgColor: "#10459E",
            data: orderSummary?.thisMonth || emptyCountAmount,
        },
        {
            title: "All Time Sales",
            bgColor: "#E99956",
            data: orderSummary?.allTime || emptyCountAmount,
        },
    ];

    const inventoryCards = [
        {
            title: "Pending Orders",
            bgColor: "#803AD4",
            count: orderSummary?.pendingOrdersCount || 0,
        },
        {
            title: "Out of Stock",
            bgColor: "#E99956",
            count: inventory?.outOfStockCount || 0,
        },
        {
            title: "Total no. of SKUs",
            bgColor: "#00A469",
            count: inventory?.totalSkus || 0,
        },
        {
            title: "Need Attention",
            bgColor: "#FC4646",
            count: inventory?.pendingApprovalCount || 0,
        },
    ];

    const ordersTable = {
        columns: [
            { Header: 'Product', accessor: 'product', width: '30%' },
            { Header: 'Quantity', accessor: 'quantity' },
            { Header: 'Amount', accessor: 'amount' },
            { Header: 'Payment', accessor: 'paymentMode' },
            {
                Header: 'Status',
                accessor: 'status',
                Cell: ({ value }: { value: string }) => (
                    <FaiBadge
                        variant="contained"
                        color={value === 'Delivered' ? 'success' : value === 'Canceled' ? 'error' : 'info'}
                        size="xs"
                        badgeContent={value}
                        container
                    />
                ),
            },
            { Header: 'Date', accessor: 'date' },
        ],
        rows: recentOrders,
    };

    if (loading) {
        return (
            <FaiBox display="flex" justifyContent="center" alignItems="center" p={4}>
                <CircularProgress />
            </FaiBox>
        );
    }

    return (
        <FaiBox className={"dashboard"}>
            <FaiBox className={"payment-overview"}>
                <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium} mb={"21px"}>
                    Payment Overview
                </FaiTypography>
            </FaiBox>
            <FaiBox className={"payment-overview-card"}>
                <Grid container spacing={2} mb={3}>
                    {paymentCards.map(card => (
                        <Grid key={card.title} item xs={12} md={6} lg={3} alignItems={"center"}>
                            <Card
                                title={card.title}
                                bgColor={card.bgColor}
                                color={palette.white.main}
                                icon={Storefront}
                                amount={card.data.amount}
                                count={card.data.count}
                            />
                        </Grid>
                    ))}
                </Grid>
            </FaiBox>
            <FaiBox className={"inventory-overview"}>
                <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium} mb={"21px"}>
                    Inventory Overview
                </FaiTypography>
                <FaiBox className={"inventory-overview-card"}>
                    <Grid container spacing={2} mb={3}>
                        {inventoryCards.map(card => (
                            <Grid key={card.title} item xs={12} md={6} lg={3} alignItems={"center"}>
                                <InventoryCard
                                    title={card.title}
                                    bgColor={card.bgColor}
                                    count={card.count}
                                    icon={Storefront}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </FaiBox>
            </FaiBox>
            <FaiBox className={"inventory-overview"}>
                <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium} mb={"21px"}>
                    Recent Orders
                </FaiTypography>
                <FaiBox className={"inventory-overview-card"}>
                    {recentOrders.length === 0 ? (
                        <FaiTypography variant="body2" color="text.secondary" p={2}>
                            No orders yet.
                        </FaiTypography>
                    ) : (
                        <DataTable
                            table={ordersTable}
                            pagination={{
                                variant: "contained",
                                color: "primary",
                            }}
                        />
                    )}
                </FaiBox>
            </FaiBox>
        </FaiBox>
    );
}

export default Dashboard;
